/**
 * Category Review Dashboard
 *
 * A dedicated page for reviewing AI-suggested category assignments that need human approval.
 * Shows all documents with pending review across the organization.
 *
 * Features:
 * - List all pending category assignments for review
 * - Filter by category, confidence, date
 * - Bulk approve/reject operations
 * - Individual assignment review with modify capability
 * - PDF preview for documents
 * - Statistics and insights
 */

import { useEffect, useState, useMemo, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  RefreshCw,
  X,
  Eye,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  categoriesApi,
  type CategoryAssignmentForReview,
  type CategoryHierarchyNode,
} from "@/services/api/categories";
import { ragApi } from "@/services/api/rag";
import { CategoryAssignmentCard } from "@/components/portal/shared/CategoryAssignmentCard";
import { SimplePDFViewer } from "@/components/portal/shared/SimplePDFViewer";
import { usePaginatedData } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/knowledgebase-file/category-review")({
  component: CategoryReviewDashboard,
});

// Cache for document details to avoid repeated API calls
interface DocumentCache {
  [documentId: string]: {
    title: string;
    fileName: string;
    pdfUrl: string;
  };
}

function CategoryReviewDashboard() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const canEditKbFile = can("edit", "kb-file");
  const organisationId = selectedOrganisation?.organisationId || "";

  const [categories, setCategories] = useState<CategoryHierarchyNode[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [confidenceFilter, setConfidenceFilter] = useState<string>("all");
  const [isProcessing, setIsProcessing] = useState(false);

  // PDF Viewer state
  const [selectedAssignment, setSelectedAssignment] =
    useState<CategoryAssignmentForReview | null>(null);
  const [documentCache, setDocumentCache] = useState<DocumentCache>({});
  const [loadingDocument, setLoadingDocument] = useState(false);

  // Confidence filter to API params
  const minConfidence =
    confidenceFilter === "low"
      ? 0
      : confidenceFilter === "medium"
        ? 0.5
        : confidenceFilter === "high"
          ? 0.8
          : undefined;
  const maxConfidence =
    confidenceFilter === "low"
      ? 0.5
      : confidenceFilter === "medium"
        ? 0.8
        : undefined;

  // Server-side pagination
  const fetchAssignments = useCallback(
    async (page: number, pageSize: number) => {
      if (!token || !organisationId) {
        return { data: [], totalCount: 0, page: 1, pageSize };
      }

      const response = await categoriesApi.review.list(token, {
        for_review: true,
        reviewed: false,
        min_confidence: minConfidence,
        max_confidence: maxConfidence,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      return {
        data: response.items,
        totalCount: response.total,
        page: response.page,
        pageSize: response.per_page,
      };
    },
    [token, organisationId, minConfidence, maxConfidence],
  );

  const {
    data: assignments,
    isLoading: loading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<CategoryAssignmentForReview>({
    fetchFn: fetchAssignments,
    initialPageSize: 25,
    dependencies: [
      token,
      organisationId,
      minConfidence,
      maxConfidence,
    ],
  });

  // Load category hierarchy
  const loadCategories = useCallback(async () => {
    if (!token || !organisationId) return;

    try {
      const response = await categoriesApi.hierarchy.get(
        token,
        organisationId,
        true,
      );
      setCategories(response.root_categories);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  }, [token, organisationId]);

  useEffect(() => {
    if (token && organisationId) {
      loadCategories();
    }
  }, [token, organisationId, loadCategories]);

  // Load document details and PDF URL for preview
  const loadDocumentForPreview = useCallback(
    async (assignment: CategoryAssignmentForReview) => {
      if (!token) return;

      setSelectedAssignment(assignment);

      // Check cache first
      if (documentCache[assignment.document_id]) {
        return;
      }

      setLoadingDocument(true);
      try {
        // Fetch presigned download URL
        const urlResponse = await ragApi.getDocumentDownloadUrl(
          token,
          assignment.document_id,
          organisationId,
          3600,
        );
        const pdfUrl = urlResponse.download_url;

        // Cache the results
        setDocumentCache((prev) => ({
          ...prev,
          [assignment.document_id]: {
            title: assignment.document_title || "Unknown",
            fileName: assignment.document_filename || "Unknown",
            pdfUrl,
          },
        }));
      } catch (error: unknown) {
        // Check if document is not in S3 (expected for some documents)
        const errorMessage = error instanceof Error ? error.message : "";
        const isS3NotAvailable =
          errorMessage.toLowerCase().includes("not stored in s3") ||
          errorMessage.toLowerCase().includes("not available");

        // Cache as unavailable so we don't retry and show "Preview not available"
        setDocumentCache((prev) => ({
          ...prev,
          [assignment.document_id]: {
            title: assignment.document_title || "Unknown",
            fileName: assignment.document_filename || "Unknown",
            pdfUrl: "", // Empty URL indicates unavailable
          },
        }));

        // Only show toast for unexpected errors, not S3 unavailability
        if (!isS3NotAvailable) {
          console.error("Failed to load document:", error);
          toast.error("Failed to load document preview");
        }
      } finally {
        setLoadingDocument(false);
      }
    },
    [token, documentCache, organisationId],
  );

  // Get unique categories for filtering (from current page data)
  const uniqueCategories = useMemo(() => {
    const seen = new Map<string, { id: string; name: string }>();
    assignments.forEach((assignment) => {
      const categoryId = assignment.category_id;
      const categoryName =
        assignment.category_name || assignment.category?.name;
      if (categoryId && categoryName && !seen.has(categoryId)) {
        seen.set(categoryId, { id: categoryId, name: categoryName });
      }
    });
    return Array.from(seen.values());
  }, [assignments]);

  // Client-side search filtering (backend doesn't support search)
  const filteredAssignments = useMemo(() => {
    let filtered = assignments;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.category_name?.toLowerCase().includes(query) ||
          a.category?.name?.toLowerCase().includes(query) ||
          a.document_title?.toLowerCase().includes(query) ||
          a.document_filename?.toLowerCase().includes(query) ||
          a.reasoning?.toLowerCase().includes(query),
      );
    }

    // Category filter (client-side within page)
    if (categoryFilter !== "all") {
      filtered = filtered.filter((a) => a.category_id === categoryFilter);
    }

    return filtered;
  }, [assignments, searchQuery, categoryFilter]);

  // Statistics based on current page
  const stats = useMemo(() => {
    const total = pagination.totalCount;
    const lowConfidence = assignments.filter(
      (a) => a.confidence !== undefined && a.confidence < 0.5,
    ).length;
    const mediumConfidence = assignments.filter(
      (a) =>
        a.confidence !== undefined &&
        a.confidence >= 0.5 &&
        a.confidence < 0.8,
    ).length;
    const highConfidence = assignments.filter(
      (a) => a.confidence !== undefined && a.confidence >= 0.8,
    ).length;

    return {
      total,
      lowConfidence,
      mediumConfidence,
      highConfidence,
    };
  }, [assignments, pagination.totalCount]);

  const handleSelectAll = () => {
    if (selectedIds.size === filteredAssignments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssignments.map((a) => a.id)));
    }
  };

  const handleSelectAssignment = (assignmentId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(assignmentId)) {
        next.delete(assignmentId);
      } else {
        next.add(assignmentId);
      }
      return next;
    });
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0 || !token) return;

    setIsProcessing(true);
    try {
      await categoriesApi.review.bulkReview(token, {
        organisation_id: organisationId,
        assignment_ids: Array.from(selectedIds),
        action: "approved",
      });

      toast.success(`Approved ${selectedIds.size} assignments`);
      setSelectedIds(new Set());
      refresh();
    } catch (error) {
      console.error("Failed to bulk approve:", error);
      toast.error("Failed to approve assignments");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0 || !token) return;

    setIsProcessing(true);
    try {
      await categoriesApi.review.bulkReview(token, {
        organisation_id: organisationId,
        assignment_ids: Array.from(selectedIds),
        action: "rejected",
      });

      toast.success(`Rejected ${selectedIds.size} assignments`);
      setSelectedIds(new Set());
      refresh();
    } catch (error) {
      console.error("Failed to bulk reject:", error);
      toast.error("Failed to reject assignments");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = async (assignmentId: string) => {
    if (!token) return;
    try {
      await categoriesApi.assignments.update(
        token,
        assignmentId,
        {
          review_action: "approved",
          reviewed: true,
        },
      );

      toast.success("Assignment approved");
      refresh();
    } catch (error) {
      console.error("Failed to approve:", error);
      toast.error("Failed to approve assignment");
    }
  };

  const handleReject = async (assignmentId: string) => {
    if (!token) return;
    try {
      await categoriesApi.assignments.delete(
        token,
        assignmentId,
      );

      toast.success("Assignment rejected");
      refresh();
    } catch (error) {
      console.error("Failed to reject:", error);
      toast.error("Failed to reject assignment");
    }
  };

  const handleModify = async (assignmentId: string, newCategoryId: string) => {
    if (!token) return;
    try {
      await categoriesApi.assignments.update(
        token,
        assignmentId,
        {
          new_category_id: newCategoryId,
          review_action: "modified",
          reviewed: true,
        },
      );

      toast.success("Assignment updated");
      refresh();
    } catch (error) {
      console.error("Failed to modify:", error);
      toast.error("Failed to update assignment");
    }
  };

  const handleRemove = async (assignmentId: string) => {
    if (!token) return;
    try {
      await categoriesApi.assignments.delete(
        token,
        assignmentId,
      );

      toast.success("Assignment removed");
      refresh();
    } catch (error) {
      console.error("Failed to remove:", error);
      toast.error("Failed to remove assignment");
    }
  };

  if (!organisationId) {
    return (
      <div className="space-y-6 p-6">
        <div className="text-center py-12 text-muted-foreground">
          Please select an organisation
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900">
            Category Review
          </h1>
          <p className="text-muted-foreground mt-1">
            Review AI-suggested category assignments that need human approval
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-full"
          onClick={refresh}
          disabled={loading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between pb-2">
            <span className="text-sm font-medium text-slate-600">
              Pending Reviews
            </span>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{stats.total}</div>
          <p className="text-xs text-muted-foreground">
            Total assignments to review
          </p>
        </div>

        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between pb-2">
            <span className="text-sm font-medium text-slate-600">
              Low Confidence
            </span>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </div>
          <div className="text-2xl font-bold text-destructive">
            {stats.lowConfidence}
          </div>
          <p className="text-xs text-muted-foreground">&lt; 50% confidence</p>
        </div>

        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between pb-2">
            <span className="text-sm font-medium text-slate-600">
              Medium Confidence
            </span>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </div>
          <div className="text-2xl font-bold text-yellow-500">
            {stats.mediumConfidence}
          </div>
          <p className="text-xs text-muted-foreground">50-80% confidence</p>
        </div>

        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between pb-2">
            <span className="text-sm font-medium text-slate-600">
              High Confidence
            </span>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-green-500">
            {stats.highConfidence}
          </div>
          <p className="text-xs text-muted-foreground">&gt; 80% confidence</p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="search">Search</Label>
            <SearchInput
              id="search"
              placeholder="Search by category, document, or reasoning..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger id="category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {uniqueCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confidence">Confidence Level</Label>
            <Select
              value={confidenceFilter}
              onValueChange={setConfidenceFilter}
            >
              <SelectTrigger id="confidence">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="low">Low (&lt; 50%)</SelectItem>
                <SelectItem value="medium">Medium (50-80%)</SelectItem>
                <SelectItem value="high">High (&gt; 80%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {canEditKbFile && selectedIds.size > 0 && (
        <div className="rounded-3xl border border-primary bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {selectedIds.size} assignment
                {selectedIds.size > 1 ? "s" : ""} selected
              </p>
              <p className="text-sm text-muted-foreground">
                Choose an action to apply to all selected assignments
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="default"
                className="rounded-full"
                onClick={handleBulkApprove}
                disabled={isProcessing}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve All
              </Button>
              <Button
                variant="destructive"
                className="rounded-full"
                onClick={handleBulkReject}
                disabled={isProcessing}
              >
                <X className="h-4 w-4 mr-2" />
                Reject All
              </Button>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setSelectedIds(new Set())}
                disabled={isProcessing}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content: Assignments List + PDF Viewer */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Assignments List */}
        <div className="rounded-3xl border border-slate-100 bg-white shadow-sm lg:col-span-1">
          <div className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">
                  Pending Assignments ({filteredAssignments.length} of{" "}
                  {assignments.length})
                </h2>
                <p className="text-sm text-muted-foreground">
                  Review and approve AI-suggested category assignments
                </p>
              </div>
              <div className="flex gap-2">
                {canEditKbFile && filteredAssignments.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={handleSelectAll}
                  >
                    {selectedIds.size === filteredAssignments.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="px-6 pb-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredAssignments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">All caught up!</h3>
                <p className="text-muted-foreground text-center">
                  {assignments.length === 0
                    ? "No category assignments need review at this time."
                    : "No assignments match your current filters."}
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] md:max-h-[600px] overflow-y-auto">
                {filteredAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                      selectedAssignment?.id === assignment.id
                        ? "bg-muted"
                        : "hover:bg-muted/50"
                    }`}
                  >
                    {canEditKbFile && (
                      <Checkbox
                        checked={selectedIds.has(assignment.id)}
                        onCheckedChange={() =>
                          handleSelectAssignment(assignment.id)
                        }
                        className="mt-4"
                      />
                    )}
                    <div className="flex-1">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium truncate max-w-[300px]">
                            {documentCache[assignment.document_id]?.title ||
                              assignment.document_title ||
                              assignment.document_filename ||
                              assignment.document_id}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadDocumentForPreview(assignment)}
                          className="shrink-0"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                      </div>
                      <CategoryAssignmentCard
                        assignment={assignment}
                        categories={categories}
                        showReviewActions={canEditKbFile}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onModify={handleModify}
                        onRemove={handleRemove}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* PDF Viewer Panel */}
        <div className="rounded-3xl border border-slate-100 bg-white shadow-sm lg:col-span-1 h-[400px] md:h-[600px] lg:h-[700px]">
          <div className="p-6 pb-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Document Preview
            </h2>
            {selectedAssignment &&
              documentCache[selectedAssignment.document_id] && (
                <p className="text-sm text-muted-foreground truncate">
                  {documentCache[selectedAssignment.document_id]?.title ||
                    documentCache[selectedAssignment.document_id]?.fileName}
                </p>
              )}
          </div>
          <div className="px-6 pb-6 h-[calc(100%-80px)]">
            {!selectedAssignment ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-sm">
                  Select an assignment to preview the document
                </p>
                <p className="text-xs mt-1">
                  Click &quot;Preview&quot; on any assignment
                </p>
              </div>
            ) : loadingDocument ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : documentCache[selectedAssignment.document_id]?.pdfUrl ? (
              <SimplePDFViewer
                url={documentCache[selectedAssignment.document_id].pdfUrl}
                title={documentCache[selectedAssignment.document_id]?.title}
              />
            ) : documentCache[selectedAssignment.document_id] ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-sm font-medium">Preview not available</p>
                <p className="text-xs mt-1 text-center max-w-xs">
                  The original file is not stored. You can still review and
                  approve the category assignment.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <AlertCircle className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-sm">Failed to load document preview</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        pageSizeOptions={pageSizeOptions}
        startIndex={startIndex}
        endIndex={endIndex}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        onPageChange={goToPage}
        onPageSizeChange={setPageSize}
        onFirstPage={firstPage}
        onLastPage={lastPage}
        onPrevPage={prevPage}
        onNextPage={nextPage}
      />
    </div>
  );
}
